<template>
    <UiCoreNavigation v-editable="blok" id="uiHeader" :logo-height="32" :blok="processedHeader" :paths="paths" />
</template>

<script setup lang="ts">
const { blok } = defineProps<{ blok: IL4HeaderContainer }>();

const { content: header }: any = blok.header;

// Needs for creating urls for top rated car parks
import { useLinksStore } from '../store/links';
import { EMobileMenuStyles } from '@nuxt-monorepo/ui/types/enums/EMobileMenuStyles';
import type { IL4HeaderContainer } from '~/../types/components/L4HeaderContainer';
import type { ILinkItem } from '@nuxt-monorepo/ui/types/components/CoreLinkList';
import type { IHeaderConfig, IMenuDropdown } from '@nuxt-monorepo/ui/types/store/global';
import { useGlobalStore } from '@nuxt-monorepo/base/app/store/global';

const linksStore = useLinksStore();
const globalStore = useGlobalStore();

watch(
    () => header,
    (value) => linksStore.updateLinks(value.menu),
    { immediate: true }
);

// Processing current L4 storyblok header data to compatible with new CoreNavigation
const processedHeader = computed(() => {
    if (header.component !== 'l4_header') return header;
    const resultHeader: IHeaderConfig = {
        FullWidth: true,
        MobileNavigation: [
            ...header.menu.map((item) => processMenuItem(item)),
            ...[
                header.account_label && header.account_link
                    ? {
                          component: 'Link',
                          Text: header.account_label,
                          _uid: `${header._uid}-account`,
                          ModalId: '',
                          Rel: [],
                          Target: '',
                          Link: { ...header.account_link },
                          trackingId: 'manage_booking',
                          _editable: header._editable,
                      }
                    : {},
            ],
            ...[
                header.help_label && header.help_link
                    ? {
                          component: 'Link',
                          Text: header.help_label,
                          _uid: `${header._uid}-help`,
                          ModalId: '',
                          Rel: [],
                          Target: '',
                          Link: { ...header.help_link },
                          trackingId: 'help',
                          _editable: header._editable,
                      }
                    : {},
            ],
        ],
        Navigation: [
            ...header.menu.map((item) => processMenuItem(item, 'desktop')),
            ...[
                header.account_label && header.account_link
                    ? {
                          component: 'Link',
                          Text: header.account_label,
                          _uid: `${header._uid}-account`,
                          ModalId: '',
                          Rel: [],
                          Target: '',
                          Link: { ...header.account_link },
                          trackingId: 'manage_booking',
                          _editable: header._editable,
                      }
                    : {},
            ],
            ...[
                header.help_label && header.help_link
                    ? {
                          component: 'Link',
                          Text: header.help_label,
                          _uid: `${header._uid}-help`,
                          ModalId: '',
                          Rel: [],
                          Target: '',
                          Link: { ...header.help_link },
                          trackingId: 'help',
                          _editable: header._editable,
                      }
                    : {},
            ],
        ],
        mobileMenuStyle: EMobileMenuStyles.UNDEFINED,
        UseDefaultLogo: header.UseDefaultLogo,
        UseMparticle: header.UseMparticle,
    };
    globalStore.updateMparticle(header.UseMparticle || false);
    return resultHeader;
});

const processMenuItem = (item, suffix = ''): IMenuDropdown | ILinkItem => {
    if (item.children?.length) {
        return {
            component: 'Dropdown',
            _uid: `${item._uid}-${suffix}`,
            _editable: item._editable,
            Title: item.text,
            Link: { ...item.url },
            ShowOnLogin: false,
            Options: item.children.map((childrenItem) => processMenuItem(childrenItem, suffix)),
            Direction: undefined,
            ColumnsCount: '',
            optionContentType: 'links',
            trackingId: item.trackingId,
        };
    } else {
        return {
            component: 'Link',
            Text: item.text as string,
            _uid: item._uid,
            ModalId: '',
            Rel: [],
            Target: '',
            Link: { ...item.url },
            trackingId: item.trackingId,
            _editable: item._editable,
        };
    }
};
</script>

<style lang="postcss" scoped>
header {
    @apply sticky top-0 left-0 right-0 z-[100];
}

.header-reference {
    @apply h-[8rem];
}

/* Begin: Header Top */
.header-top {
    @apply py-3 px-3 bg-white static tablet:relative z-10;

    &__container {
        @apply flex justify-between items-center;
    }

    &__logo img,
    &__logo svg {
        @apply w-[250px] h-[34px] tablet:h-16 tablet:w-auto;
    }

    &__actions {
        @apply pl-8 hidden tablet:flex;
    }

    &__action {
        @apply px-2;

        &:not(:first-child) {
            @apply border-l border-l-primary -ml-[1px] pr-3;
        }

        &:last-child {
            /* Increased padding left to adjust tooltop position on near tablet and lg resolutions and prevent horizontal scroll */
            @apply pr-3;
        }

        .header-tooltip {
            @apply block relative;

            .iconify {
                @apply block w-[35px] h-5 bg-primary;
            }

            &:before,
            &:after {
                @apply absolute opacity-0 transition-opacity duration-200 top-full mt-1.5 left-1/2;
            }

            &:before {
                content: '';
                @apply w-8 h-8 bg-gray-300 rounded-full -translate-x-2/4 -translate-y-full;
            }

            &:after {
                content: attr(data-label);
                @apply text-white rounded bg-gray-500 min-w-[80px] px-1.5 py-1 mt-3 leading-4 left-1/2 -translate-x-2/4 text-sm text-center pointer-events-none;
            }

            &:hover {
                &::before,
                &:after {
                    @apply opacity-100;
                }
                .iconify {
                    @apply bg-gray-500;
                }
            }
        }
    }

    &__toggler {
        @apply tablet:hidden z-50 relative;
    }
}

/* End: Header Top */

/* Begin: Header Navigation */
.header-menu {
    height: calc(100vh - var(--menuTop));
    @apply absolute md:h-auto tablet:relative top-0 left-0 right-0 bg-primary w-full pt-16 tablet:py-3 overflow-hidden tablet:overflow-visible transition-transform duration-200 -translate-x-full tablet:translate-x-0;

    .is-menu-open & {
        @apply translate-x-0;
    }

    &__list {
        @apply flex flex-col tablet:flex-row px-4 tablet:px-0;
    }

    &__submenu-btn {
        @apply flex tablet:hidden relative py-2 text-white uppercase w-full text-xl;

        .iconify {
            @apply overflow-hidden absolute top-1/2 right-0 -translate-y-2/4 text-3xl;
        }
    }

    &__item {
        @apply text-white fixed tablet:relative pl-16 pt-5 tablet:pl-0 tablet:pt-0 tablet:mr-10 hidden tablet:flex flex-col tablet:flex-row inset-0 bg-[#734486] tablet:bg-transparent translate-x-full tablet:translate-x-0 transition-transform duration-200;

        &--single {
            @apply flex translate-x-0 relative pl-0 uppercase tablet:normal-case bg-transparent py-2 tablet:py-0;
        }

        &.is-open {
            @apply flex translate-x-0 z-30;
        }

        &:hover {
            .header-menu__link {
                @apply underline;
            }

            .header-menu__dropdown {
                @apply flex;

                &.two-columns {
                    @apply min-w-[478px];
                }
            }

            .header-menu__dropdown-wrapper {
                @apply flex tablet:flex-wrap flex-col tablet:max-h-[480px];
            }
        }
    }

    &__link {
        @apply text-xl tablet:text-base uppercase tablet:normal-case whitespace-pre-wrap;

        &.has-dropdown {
            @apply pl-6 relative;

            &::before {
                content: '';
                @apply hidden tablet:block absolute left-0 top-[0.3rem] h-2 w-2 border-white border-r-2 border-b-2 box-border rotate-45;
            }
        }
    }

    &__sublink {
        @apply px-2 py-1 tablet:bg-[#f7f2f9] text-base tablet:text-sm min-w-[230px] uppercase tablet:normal-case whitespace-pre-wrap;

        &:nth-of-type(2n + 1) {
            @apply tablet:bg-white;
        }

        &:hover {
            @apply bg-primary text-white;
        }
    }

    &__mobile-back-btn {
        @apply absolute tablet:hidden bg-primary left-0 top-0 h-full w-[60px];

        .iconify {
            @apply w-[35px] h-[25px] overflow-hidden absolute top-5 left-4;
            background-size: 514px 739px;
            background-position-x: -98px;
            background-position-y: -343px;
        }
    }

    &__dropdown {
        @apply relative tablet:absolute tablet:hidden top-4 tablet:top-8 p-2 flex flex-col tablet:bg-white tablet:border tablet:border-primary tablet:text-primary tablet:ring-4 tablet:ring-[#e9daef] tablet:ring-inset tablet:shadow whitespace-nowrap;

        &:before {
            content: '';
            @apply absolute left-0 right-0 -top-3 h-4;
        }
    }

    &__dropdown-pointer {
        @apply relative -top-1 hidden tablet:block;

        &:before,
        &:after {
            content: ' ';
            @apply absolute left-8 bottom-full w-0 h-0 pointer-events-none;
        }

        &:before {
            border-color: rgba(233, 218, 239, 0) rgba(233, 218, 239, 0) #e9daef;
            @apply ml-[16px] border-[16px];
        }

        &:after {
            @apply ml-[22px] border-[10px] border-transparent border-b-white;
        }
    }

    &__dropdown-wrapper {
        max-height: calc(100vh - 130px - var(--menuTop));
        @apply flex flex-col overflow-y-auto overflow-x-hidden sm:max-h-[calc(100vh-130px)];
    }

    &__actions {
        @apply tablet:hidden -mx-4 pt-3 mt-3 px-4 border-t-2 border-white;
    }

    &__action {
        @apply py-2 text-white uppercase leading-6 block text-xl;
    }
}

/* End: Header Navigation */

.mobile-menu-toggler {
    @apply block relative w-10 h-10 ml-2.5 bg-transparent z-50 border-none;
    text-indent: -9999px;

    &:before,
    &:after,
    span {
        @apply bg-primary rounded absolute left-1/4 right-1/4 h-1 -mt-0.5 duration-200 transition-all ease-linear;
    }

    &:before,
    &:after {
        content: '';
    }

    &:before {
        @apply top-[30%];
    }

    &:after {
        @apply top-[70%];
    }

    span {
        top: 50%;

        .is-open & {
            @apply opacity-0;
        }
    }

    &.is-open {
        &:before,
        &:after {
            @apply bg-white top-1/2 left-[15%] right-[15%];
        }

        &:before {
            @apply rotate-45;
        }

        &:after {
            @apply -rotate-45;
        }

        span {
            @apply opacity-0;
        }
    }
}
#uiHeader {
    @apply bg-white px-0;
    :deep {
        .navigation {
            &__wrapper {
                @apply lg:flex-wrap lg:gap-0 px-4;
            }

            &__logo {
                @apply h-10;
                & > * {
                    @apply max-h-full w-auto;
                }
            }

            &__menu {
                @apply lg:justify-end;

                li {
                    & > * {
                        @apply lg:min-h-12;
                    }
                }

                .current-page-nav-item > a,
                .current-page-nav-item > .dropdown-item > a,
                .current-page-nav-item a.dropdown-item__title {
                    @apply text-primary;
                }
            }

            &__nav {
                @apply lg:pl-4;
            }
        }
    }
}
</style>

<style lang="postcss">
body.is-menu-open {
    @apply overflow-hidden h-screen sm:overflow-auto sm:h-auto;
}
</style>
